import axios, {AxiosResponse} from 'axios';
import {CategoryTreeItem, CategoryTreeNickname, ItemTaxonomy} from '../models';

const API_URL = process.env.REACT_APP_CATEGORY_API_URL;
const GET_CATEGORIES_URL = `${API_URL}/category-tree`;

type PaginationResponse = {
  totalRecords: number;
  recordsPerPage: number;
  totalPages: number;
  currentPage: number;
  nextPage: number | null;
  prevPage: number | null;
};

export const listItemTaxonomy = async (
  query: string
): Promise<{data: ItemTaxonomy[]; pagination: PaginationResponse}> => {
  return axios.get(`${GET_CATEGORIES_URL}/item-with-taxonomy?${query}`).then(
    (
      resp: AxiosResponse<{
        data: ItemTaxonomy[];
        pagination: PaginationResponse;
      }>
    ) => resp.data
  );
};

const adaptQuery = (query?: string): string => {
  const params = new URLSearchParams(query || '');
  if (params.has('items_per_page')) {
    params.set('take', params.get('items_per_page') as string);
    params.delete('items_per_page');
  }
  return params.toString();
};

export const listCategoryNicknames = async (
  query?: string
): Promise<{result: CategoryTreeNickname[]; resultCount: number}> => {
  const response = await axios.get(
    `${GET_CATEGORIES_URL}/nickname?${adaptQuery(query)}`
  );
  return {
    result: response.data.data,
    resultCount: response.data.pagination.total_records,
  };
};

export const listCategoryItems = async (
  query?: string
): Promise<{result: CategoryTreeItem[]; resultCount: number}> => {
  const response = await axios.get(
    `${GET_CATEGORIES_URL}/item?${adaptQuery(query)}`
  );
  return {
    result: response.data.data,
    resultCount: response.data.pagination.total_records,
  };
};

// --- Nickname ---

export type NicknameUpsertPayload = {
  name: string;
  type: string;
  item_hash: string;
  hash?: string | null;
};

export const getNicknameTypes = async (): Promise<{name: string}[]> => {
  const response = await axios.get(`${GET_CATEGORIES_URL}/nickname-type`);
  return response.data.data;
};

export const upsertCategoryNickname = async (
  payload: NicknameUpsertPayload
): Promise<void> => {
  const body = payload.hash
    ? payload
    : {name: payload.name, type: payload.type, item_hash: payload.item_hash};
  await axios.post(`${GET_CATEGORIES_URL}/nickname`, body);
};

export const deleteCategoryNickname = async (hash: string): Promise<void> => {
  await axios.delete(`${GET_CATEGORIES_URL}/nickname/${hash}`);
};

// --- Item ---

export type CategoryItemUpsertPayload = {
  name: string;
  type_hash: string;
  sub_system_hash?: string | null;
  hash?: string | null;
};

export type CategoryTypeOption = {
  hash: string;
  name: string;
  slug: string;
  group_hash: string;
  group_name: string;
  family_hash: string;
  family_name: string;
};

export type CategorySubSystemOption = {
  hash: string;
  name: string;
  slug: string;
  system: {hash: string; name: string; slug: string};
};

export const searchCategoryTypes = async (
  name: string
): Promise<CategoryTypeOption[]> => {
  const response = await axios.get(
    `${GET_CATEGORIES_URL}/type?name=${encodeURIComponent(name)}`
  );
  return response.data;
};

export const searchCategorySubSystems = async (
  name?: string
): Promise<CategorySubSystemOption[]> => {
  const params = name ? `?name=${encodeURIComponent(name)}` : '';
  const response = await axios.get(`${GET_CATEGORIES_URL}/sub-system${params}`);
  return response.data.data;
};

export const upsertCategoryItem = async (
  payload: CategoryItemUpsertPayload
): Promise<void> => {
  const body = payload.hash
    ? payload
    : {
        name: payload.name,
        type_hash: payload.type_hash,
        sub_system_hash: payload.sub_system_hash,
      };
  await axios.post(`${GET_CATEGORIES_URL}/item`, body);
};

export const deleteCategoryItem = async (hash: string): Promise<void> => {
  await axios.delete(`${GET_CATEGORIES_URL}/item/${hash}`);
};

// --- Legacy ---

export const upsertItemTaxonomy = async (
  itemTaxonomy: Partial<
    ItemTaxonomy & {item_hash: string; ncm_code: string; cest: string}
  >
): Promise<ItemTaxonomy> => {
  return axios
    .put(`${GET_CATEGORIES_URL}/taxonomy/upsert`, itemTaxonomy)
    .then((resp: AxiosResponse<ItemTaxonomy>) => resp.data);
};
